import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const BrandingContext = createContext(null);

const DEFAULT_BRANDING = {
  name: 'My Learning Graph',
  logoSmall: null,
  logoMedium: null,
  primaryColor: '#1A1A2E',
  secondaryColor: '#6C47FF',
  planType: null,
};

export function BrandingProvider({ children }) {
  const { user } = useAuth();
  const [rawBranding, setRawBranding] = useState(DEFAULT_BRANDING);
  // Fetched live from the server rather than read off the JWT, so a
  // super-admin toggle takes effect on the next page load instead of
  // waiting for the student's 7-day token to expire and force a re-login.
  // Tri-state: null = not yet known (still loading), true/false = resolved.
  // Starting at `true` used to make a student's real coaching branding flash
  // on screen for a moment on every load before possibly disappearing once
  // the real (off) value arrived -- worse than the rare fetch-failure case
  // fail-open was meant to protect against.
  const [showCoachingBranding, setShowCoachingBranding] = useState(null);

  useEffect(() => {
    setRawBranding(user?.branding || DEFAULT_BRANDING);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get('/settings/branding')
      .then((r) => { if (!cancelled) setShowCoachingBranding(r.data.showCoachingBranding !== false); })
      .catch(() => { if (!cancelled) setShowCoachingBranding(true); }); // fail open -- only on a genuine error, never while merely pending
    return () => { cancelled = true; };
  }, [user]);

  // Only the student portal respects the platform-wide toggle -- coaching
  // admins/teachers/super admin always see the real branding regardless,
  // since they need to know which coaching they're managing. Falling all
  // the way back to DEFAULT_BRANDING (not just clearing name/logoSmall)
  // means the primary/secondary colors revert to the default scheme too,
  // for every consumer that reads them off this same object (Sidebar's
  // background, Dashboard's welcome-banner gradient, etc.) with no changes
  // needed in any of those files. `!== true` (not `!== false`) means the
  // still-loading null state hides branding too, matching Login.jsx's
  // Admin-card fix: don't show the gated content until it's actually known.
  const hideForStudent = user?.role === 'student' && showCoachingBranding !== true;
  // Super admin has no coaching context, so it should always reflect the
  // current platform default -- not a name snapshot baked into whatever JWT
  // happened to be issued at their last login. Without this, a super admin
  // logged in from before a rebrand keeps seeing the old name in their own
  // sidebar until they manually re-log-in, even though every fresh session
  // (any other role) already shows the new name immediately.
  const branding = user?.role === 'super_admin'
    ? DEFAULT_BRANDING
    : hideForStudent ? DEFAULT_BRANDING : rawBranding;

  useEffect(() => {
    // Apply CSS variables globally
    document.documentElement.style.setProperty('--color-primary', branding.primaryColor || DEFAULT_BRANDING.primaryColor);
    document.documentElement.style.setProperty('--color-secondary', branding.secondaryColor || DEFAULT_BRANDING.secondaryColor);
  }, [branding]);

  return <BrandingContext.Provider value={branding}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  return useContext(BrandingContext) || DEFAULT_BRANDING;
}
